/**
 * Skill effects as cached pixel-art sprite sheets: weapon trails, sword qi, palm force waves, ground
 * slams, heals and buffs, hit sparks, the parry clash, the 破 shatter and the enemy wind-up tell.
 *
 * Each effect is a field of light over the frame (how strongly each point glows at phase `p`,
 * 0–1, of the effect), drawn once per frame into a small canvas (on the figure grid for effects that
 * cling to a figure, on the world grid for the rest: `CLINGS`) and kept: on screen an effect is one `drawImage` of the frame for its phase, whatever its shape.
 * The light is quantised into four hard tones of the element's ramp (no smooth gradients), and lit
 * like the figures: edges facing the warm key light (upper right) a tone brighter, edges turned
 * away a tone darker and cooler. Every effect plays its `FRAMES` frames on a continuous phase.
 */
import { iceDef, ICE_RAMP } from './iceFx';
import { density1x } from './density';
import { canvas, ctx2d } from './pixel';
import type { FxElement, FxWeapon, SwingStyle } from './skillFx';
import { FRAMES } from './sprites';

type RGB = readonly [number, number, number];

/** Four tones of an effect's light, dark to bright: cool shade, body, lit, white-hot core. */
interface Ramp {
  shade: RGB;
  body: RGB;
  light: RGB;
  core: RGB;
}

/** Element ramps in natural dyes and materials: no pure saturated hues. */
export const RAMPS: Readonly<Record<FxElement, Ramp>> = {
  metal: { shade: [70, 86, 116], body: [150, 172, 196], light: [214, 226, 236], core: [252, 250, 242] },
  fire: { shade: [112, 44, 42], body: [206, 98, 48], light: [244, 170, 88], core: [255, 238, 196] },
  poison: { shade: [58, 44, 78], body: [106, 148, 64], light: [182, 210, 112], core: [234, 244, 198] },
  earth: { shade: [74, 62, 64], body: [158, 122, 78], light: [212, 178, 124], core: [246, 228, 190] },
  water: { shade: [42, 62, 104], body: [100, 156, 200], light: [178, 216, 238], core: [244, 250, 255] },
  wind: { shade: [56, 84, 96], body: [122, 178, 146], light: [194, 230, 204], core: [244, 252, 240] },
  thunder: { shade: [56, 48, 112], body: [128, 150, 226], light: [200, 216, 255], core: [255, 255, 250] },
  yin: { shade: [48, 30, 70], body: [124, 78, 148], light: [196, 156, 214], core: [244, 228, 248] },
  yang: { shade: [118, 76, 44], body: [212, 158, 70], light: [242, 210, 132], core: [255, 248, 222] },
  blood: { shade: [70, 20, 34], body: [158, 36, 40], light: [214, 92, 82], core: [250, 210, 196] },
  ink: { shade: [18, 22, 34], body: [40, 52, 72], light: [96, 128, 164], core: [214, 226, 238] },
  heal: { shade: [52, 92, 84], body: [118, 186, 132], light: [206, 228, 150], core: [252, 248, 214] },
};

/** The enemy wind-up tells: a true attack in red, a guard break (破) in amber. */
export const TELL_RAMPS = {
  attack: { shade: [96, 22, 34], body: [186, 48, 50], light: [232, 108, 88], core: [255, 218, 192] },
  break: { shade: [112, 52, 26], body: [212, 120, 42], light: [244, 178, 90], core: [255, 236, 200] },
} as const satisfies Record<string, Ramp>;

/** The body colour of an element as `r,g,b` (for particles and flashes beside a sheet). */
export const elementRgb = (e: FxElement): string => RAMPS[e].body.join(',');

const mix = (a: RGB, b: RGB, t: number): RGB => [
  Math.round(a[0] + (b[0] - a[0]) * t),
  Math.round(a[1] + (b[1] - a[1]) * t),
  Math.round(a[2] + (b[2] - a[2]) * t),
];

const RAMP_OF = new Map<string, Ramp>();
/**
 * A ramp around any `r,g,b` colour: shade dropped dark and toward cool blue, light and core toward
 * the warm key light.
 */
export function rampOf(col: string): Ramp {
  let r = RAMP_OF.get(col);
  if (!r) {
    const c = col.split(',').map(Number) as unknown as RGB;
    r = {
      shade: mix(mix(c, [0, 0, 0], 0.55), [40, 52, 96], 0.35),
      body: c,
      light: mix(c, [255, 244, 224], 0.45),
      core: mix(c, [255, 252, 240], 0.85),
    };
    RAMP_OF.set(col, r);
  }
  return r;
}

/** Kinds of sheet. */
export type SheetKind =
  /** A weapon's trail through a swing (`arc` picks the sweep, `weapon` the shape). */
  | 'slash'
  /** A straight thrust of light (a sword point, or a spear's long twisting wedge). */
  | 'thrust'
  /** A ghostly palm and the ripples of its force, at the caster's front. */
  | 'palm'
  /** A palm's force wave flying to the target. */
  | 'palmWave'
  /** A crescent of sword qi flying to the target (`dir` its heading). */
  | 'qi'
  /** A ring of force across the ground, cracks and debris. */
  | 'slam'
  /** A healing circle with motes rising. */
  | 'heal'
  /** Two bands of qi spiralling up round the body. */
  | 'buff'
  /** A hit's spark (sized by the blow). */
  | 'spark'
  /** Steel meeting steel on a parried blow. */
  | 'clash'
  /** A guard shattered by a break (破): shards of the qi shield flung out. */
  | 'shatter'
  /** An enemy's wind-up: the ground it will strike, filling as the blow gathers (`dir` its heading). */
  | 'tell'
  /** A ring of chain links closing round the target (a shackle). */
  | 'shackle'
  /** A qin's notes: arcs of sound spreading forward. */
  | 'sound'
  /** A smoke bomb's cloud billowing up. */
  | 'smoke'
  /** Caltrops strewn across the ground. */
  | 'caltrops'
  /** A dharma wheel turning on the ground (`r` its radius). */
  | 'dharma'
  // เหมันต์สะกดธาร's ice (render/iceFx.ts): a frost crescent per cut (`arc` the cut), crystals bursting
  // from a foe, frost spreading on the ground (`r` its radius), the ice a frozen foe is held in,
  // that ice shattering, and frost gathering on the blade.
  | 'iceArc'
  | 'iceShards'
  | 'frost'
  | 'iceShell'
  | 'iceShatter'
  | 'frostGlint';

export interface SheetSpec {
  kind: SheetKind;
  /** Colour: an `r,g,b` string, or a ramp by name (an element, or a tell). */
  tint: string;
  /** Size (bucketed to quarters). */
  size: number;
  /** Mirrored (facing screen-left); the light stays upper right. */
  flip?: boolean;
  weapon?: FxWeapon;
  arc?: SwingStyle;
  /** Heading on screen in sixteenths of a turn (qi); for a tell, −1 for all round. */
  dir?: number;
  /** A tell for a break (jagged, amber) rather than an attack. */
  brk?: boolean;
  /** A slam's radius, a tell's reach (art pixels at size 1). */
  r?: number;
}

/** Light at a point (art pixels from the anchor, facing right, y down), 0–1. */
export type Field = (u: number, v: number) => number;

export interface Def {
  /** Extent left, up, right and down of the anchor at size 1 (art pixels). */
  box: readonly [number, number, number, number];
  /** Canvas pixels per art pixel: 2 (the figures') unless a broad ground decal, drawn at 1 like the ground. */
  res?: number;
  field: (p: number) => Field;
  /** Opacity of each tone (none, shade, body, lit, core), over `ALPHA`: see-through ice. */
  alpha?: readonly [number, number, number, number, number];
  /** A one art pixel rim of the shade tone round everything lit (ice's dark outline). */
  outline?: boolean;
}

const TAU = Math.PI * 2;
/** A repeatable pseudo-random 0–1 for an index. */
const hash = (i: number): number => {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const clamp01 = (x: number) => Math.max(0, Math.min(1, x));
/** Fades out over the last part of an effect, from `from`. */
const fadeAfter = (p: number, from: number) => (p < from ? 1 : 1 - (p - from) / (1 - from));
/** Distance from a point to a segment. */
function segDist(u: number, v: number, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const t = clamp01(((u - x0) * dx + (v - y0) * dy) / (dx * dx + dy * dy || 1));
  return Math.hypot(u - x0 - dx * t, v - y0 - dy * t);
}

/** Sweep of each swing style: from angle `a0` to `a1` round an ellipse (facing right, y down). */
const SWEEPS: Partial<Record<SwingStyle, { a0: number; a1: number; k: number; ky?: number }>> = {
  cleave: { a0: -2.0, a1: 1.0, k: 1 },
  rising: { a0: 1.1, a1: -1.7, k: 1 },
  overhead: { a0: -2.3, a1: 0.9, k: 1.15, ky: 1.2 },
  leap: { a0: -2.4, a1: 1.3, k: 1.25, ky: 1.3 },
  ultimate: { a0: -2.5, a1: 1.2, k: 1.4, ky: 1.25 },
  qi: { a0: -1.4, a1: 0.9, k: 1.1, ky: 0.8 },
  spin: { a0: 0, a1: TAU, k: 1.4, ky: 0.6 },
  fan: { a0: 0, a1: TAU, k: 1.3, ky: 0.62 },
};

/** Shape of each weapon's trail: radii, thickness and how its light falls across the band. */
const BLADES: Record<
  FxWeapon,
  { rx: number; ry: number; th: number; cx: number; feel: 'edge' | 'heavy' | 'blunt' | 'ribs' | 'ribbon' }
> = {
  jian: { rx: 21, ry: 14, th: 2.8, cx: 0, feel: 'edge' },
  dao: { rx: 22, ry: 16, th: 6.5, cx: 0, feel: 'heavy' },
  spear: { rx: 30, ry: 13, th: 3.2, cx: 2, feel: 'edge' },
  polearm: { rx: 30, ry: 15, th: 6, cx: 2, feel: 'heavy' },
  fan: { rx: 20, ry: 14, th: 7, cx: 0, feel: 'ribs' },
  staff: { rx: 24, ry: 15, th: 5.5, cx: 0, feel: 'blunt' },
  fist: { rx: 11, ry: 10, th: 4, cx: 9, feel: 'blunt' },
  claw: { rx: 12, ry: 10, th: 4, cx: 8, feel: 'ribs' },
  sleeve: { rx: 22, ry: 15, th: 4, cx: 0, feel: 'ribbon' },
};

function slashDef(weapon: FxWeapon, arc: SwingStyle): Def {
  const b = BLADES[weapon];
  const sw = SWEEPS[arc] ?? SWEEPS.cleave!;
  const whole = arc === 'spin' || arc === 'fan';
  const rx = b.rx * sw.k;
  const ry = b.ry * sw.k * (sw.ky ?? 1);
  const cx = whole ? 0 : b.cx;
  const th = b.th * (whole ? 0.8 : 1);
  const span = Math.abs(sw.a1 - sw.a0);
  const dirn = Math.sign(sw.a1 - sw.a0);
  const R = (rx + ry) / 2;
  const m = th + 3;
  return {
    box: [rx - cx + m, ry + m, rx + cx + m, ry + m],
    field: (p) => {
      // The head races round (at the end of the sweep a third of the way in), the tail drains after.
      const head = Math.min(1, p / 0.32);
      const tail = clamp01((p - 0.12) / 0.78);
      const fade = fadeAfter(p, 0.45);
      return (u, v) => {
        const ex = (u - cx) / rx;
        const ey = v / ry;
        const r = Math.hypot(ex, ey);
        if (r > 1.2 || r < 0.35) return 0;
        let d = dirn * (Math.atan2(ey, ex) - sw.a0);
        d = ((d % TAU) + TAU) % TAU;
        const t = d / span;
        if (t > head || t < tail || t > 1) return 0;
        const along = (t - tail) / Math.max(1e-3, head - tail);
        let thick = 0.6 + th * Math.pow(along, 0.6);
        if (b.feel === 'heavy') thick *= 0.78 + 0.22 * Math.sin(t * 46);
        // A sleeve is a ribbon of silk: its band ripples as it streams through the air.
        const dist = (1 - r) * R - (b.feel === 'ribbon' ? Math.sin(t * 16 - p * 12) * 2.2 : 0);
        if (dist < -0.8 || dist > thick) return 0;
        if (dist < 0) return 0.24 * fade;
        const x = dist / thick;
        let i: number;
        switch (b.feel) {
          case 'edge':
            i = 1 - Math.pow(x, 0.6) * 0.7;
            break;
          case 'heavy':
            i = x < 0.2 ? 1 : 0.72 - x * 0.35;
            break;
          case 'blunt':
            // No edge: a band of streaks, the motion of a heavy thing swung.
            i = (dist / 1.6) % 1 < 0.55 ? 0.7 - x * 0.25 : 0.3;
            break;
          case 'ribbon':
            // Silk: a lit top fold and a shaded underside, no cutting edge.
            i = x < 0.35 ? 0.85 : x < 0.7 ? 0.6 : 0.34;
            break;
          case 'ribs': {
            // Fan ribs (or claw rakes): thin bright lines with paper (nothing) between.
            const rib = Math.abs((((dist / th) * 4) % 1) - 0.5);
            i = rib > 0.32 ? 0.95 : weapon === 'fan' ? 0.36 : 0;
            break;
          }
        }
        return i * (0.5 + 0.5 * along) * fade;
      };
    },
  };
}

function thrustDef(weapon: FxWeapon): Def {
  const spear = weapon === 'spear' || weapon === 'polearm';
  const L = spear ? 46 : 32;
  return {
    box: [2, 11, L + 10, 11],
    field: (p) => {
      const grow = Math.min(1, p * 4);
      const len = L * grow;
      const fade = fadeAfter(p, 0.35);
      return (u, v) => {
        const x = u - 6;
        if (x < 0 || x > len + 2) return 0;
        const k = x / L;
        if (spear) {
          // A long wedge, broad at the hands and keen at the point, with a twist running down it.
          const half = 3.6 * (1 - k) + 0.6;
          const a = Math.abs(v);
          if (a > half + 0.7) {
            // Speed lines either side.
            const line = Math.abs(a - (half + 4)) < 0.5 && (x + p * 60) % 9 < 5;
            return line ? 0.4 * fade : 0;
          }
          if (a > half) return 0.24 * fade;
          const twist = ((x - p * 50 + v * 3) / 7) % 1;
          return (twist < 0.4 ? 1 : 0.62 - (a / half) * 0.2) * fade;
        }
        const tip = len - x < 2.5 && Math.abs(v) < 2.5;
        if (tip) return fade;
        if (Math.abs(v) < 0.75) return (0.95 - k * 0.2) * fade;
        if (Math.abs(v) < 1.8) return 0.5 * fade;
        // Speed lines above and below.
        for (const ly of [-6, 6, -9])
          if (Math.abs(v - ly) < 0.5 && x > len * 0.2 && x < len * 0.8) return 0.4 * fade;
        return 0;
      };
    },
  };
}

/** Is the point inside a palm seen upright (fingers up), `s` its scale? 1 inside, 2 on its rim. */
function palmShape(u: number, v: number, s: number): number {
  const x = u / s;
  const y = v / s;
  const palm = (x / 4.6) ** 2 + ((y - 1) / 5.2) ** 2;
  let d = Math.sqrt(palm) - 1;
  for (const [fx, top] of [
    [-3, -11],
    [-1, -12.5],
    [1.2, -12],
    [3.3, -10],
  ] as const)
    d = Math.min(d, (segDist(x, y, fx, -3, fx, top) - 0.95) / 2);
  d = Math.min(d, (segDist(x, y, -4.2, 1, -7.6, -3.5) - 1) / 2);
  return d < -0.18 ? 1 : d < 0.05 ? 2 : 0;
}

function palmDef(wave: boolean): Def {
  return {
    box: wave ? [24, 22, 18, 14] : [4, 22, 34, 18],
    field: (p) => {
      const fade = fadeAfter(p, wave ? 0.75 : 0.45);
      const s = wave ? 1.3 : 0.8 + p * 0.5;
      const cx = wave ? 0 : 12 + p * 6;
      return (u, v) => {
        const x = u - cx;
        const inPalm = palmShape(x, v, s);
        if (inPalm === 2) return 0.95 * fade;
        if (inPalm === 1) return 0.42 * fade;
        // Ripples of force: ovals spreading out ahead (or trailing behind a flying wave).
        const back = wave ? -1 : 1;
        for (let k = 0; k < 3; k++) {
          const rr = ((p * 1.6 + k / 3) % 1) * (wave ? 14 : 18) + 4;
          const ex = (x * back - rr * 0.35) / (rr * 0.45);
          const ey = (v + 3) / rr;
          const d = Math.abs(Math.hypot(ex, ey) - 1) * rr;
          if (d < 0.7 && x * back > 0) return (0.7 - k * 0.12) * fade;
        }
        return 0;
      };
    },
  };
}

function qiDef(dir: number): Def {
  const ang = (dir / 16) * TAU;
  const ca = Math.cos(ang);
  const sa = Math.sin(ang);
  return {
    box: [30, 22, 30, 22],
    field: (p) => {
      const fade = fadeAfter(p, 0.8);
      const shimmer = 0.9 + 0.1 * Math.sin(p * 40);
      return (u0, v0) => {
        // Into the crescent's own frame, its convex face toward the heading.
        const u = u0 * ca + v0 * sa;
        const v = -u0 * sa + v0 * ca;
        const outer = ((u + 1) / 6.5) ** 2 + (v / 14) ** 2;
        const inner = ((u + 5) / 5.5) ** 2 + (v / 12.5) ** 2;
        if (outer < 1 && inner > 1) {
          const edge = outer > 0.72;
          return (edge ? 1 : 0.62) * shimmer * fade;
        }
        if (outer < 1.25 && inner > 1) return 0.24 * fade;
        // The trail: streaks of qi behind it.
        if (u < -4 && u > -28) {
          const k = (-u - 4) / 24;
          const half = 11 * (1 - k);
          if (Math.abs(v) < half && Math.abs((v / 3) % 1) < 0.28 && (u - p * 80) % 8 < 5)
            return (0.48 - k * 0.3) * fade;
        }
        return 0;
      };
    },
  };
}

function slamDef(R: number): Def {
  const cracks = Array.from({ length: 8 }, (_, k) => ({
    a: (k / 8) * TAU + hash(k) * 0.5,
    l: 0.55 + hash(k + 9) * 0.4,
  }));
  const debris = Array.from({ length: 12 }, (_, k) => ({
    a: hash(k + 20) * TAU,
    sp: 0.5 + hash(k + 40) * 0.5,
    up: 6 + hash(k + 60) * 12,
  }));
  return {
    res: 1,
    box: [R + 6, R / 2 + 24, R + 6, R / 2 + 6],
    field: (p) => {
      const e = 1 - (1 - p) ** 2;
      const re = R * (0.2 + 0.8 * e);
      const band = 2 + 3 * (1 - p);
      const fade = fadeAfter(p, 0.4);
      return (u, v) => {
        // Debris thrown up and falling back.
        for (const d of debris) {
          const r = re * 0.85 * d.sp;
          const h = Math.sin(Math.min(1, p * 1.4) * Math.PI) * d.up;
          if (Math.abs(u - Math.cos(d.a) * r) < 1 && Math.abs(v - (Math.sin(d.a) * r * 0.5 - h)) < 1)
            return 0.66 * fadeAfter(p, 0.6);
        }
        const gx = u;
        const gy = v * 2;
        const d = Math.hypot(gx, gy);
        if (d > R + 4) return 0;
        const ring = Math.abs(d - re);
        if (ring < band) return (0.9 - (ring / band) * 0.45) * fade;
        // Cracks running out across the ground (dark, cool), lasting past the ring.
        const a = Math.atan2(gy, gx);
        for (const c of cracks) {
          let da = Math.abs(a - c.a);
          da = Math.min(da, TAU - da);
          const jag = Math.sin(d * 0.9 + c.a * 7) * 0.9;
          if (d < R * c.l * Math.min(1, p * 4) && Math.abs(da * d - jag) < 0.7)
            return 0.22 * fadeAfter(p, 0.7);
        }
        if (d < re) return 0.16 * fade;
        return 0;
      };
    },
  };
}

function healDef(): Def {
  const motes = Array.from({ length: 11 }, (_, k) => ({
    x: (hash(k) - 0.5) * 24,
    o: hash(k + 7),
    sp: 0.7 + hash(k + 3) * 0.6,
  }));
  return {
    box: [20, 50, 20, 10],
    field: (p) => {
      const fade = fadeAfter(p, 0.6);
      const rr = 13 + p * 4;
      return (u, v) => {
        const d = Math.hypot(u, v * 2);
        if (Math.abs(d - rr) < 1.1) return 0.85 * fade;
        // A lotus of four petals inside the circle.
        const a = Math.atan2(v * 2, u);
        const petal = rr * 0.62 * Math.abs(Math.cos(a * 2));
        if (d < petal && d > petal - 1.2) return 0.55 * fade;
        for (const m of motes) {
          const y = -((p * m.sp + m.o) % 1) * 46 * Math.min(1, p * 3);
          const x = m.x * (1 - -y / 90) + Math.sin(y * 0.2 + m.o * 6) * 1.5;
          if (Math.abs(u - x) < 0.8 && Math.abs(v - y) < 1.2) return (y < -36 ? 0.5 : 0.95) * fade;
        }
        // A faint column of light.
        if (Math.abs(u) < 7 && v < 0 && v > -40 * Math.min(1, p * 2.5)) return 0.15 * fade;
        return 0;
      };
    },
  };
}

function buffDef(): Def {
  return {
    box: [18, 54, 18, 8],
    field: (p) => {
      const fade = fadeAfter(p, 0.6);
      const top = 50 * Math.min(1, p * 2.2);
      return (u, v) => {
        const h = -v;
        if (h < -6 || h > top) return 0;
        if (h < 2) {
          const d = Math.hypot(u, v * 2);
          return Math.abs(d - 14) < 1 ? 0.7 * fade : 0;
        }
        const k = h / 50;
        for (const off of [0, Math.PI]) {
          const a = h * 0.24 + p * 10 + off;
          const x = Math.cos(a) * 13 * (1 - k * 0.45);
          const y = -h + Math.sin(a) * 4;
          if (Math.hypot(u - x, (v - y) * 1.2) < 1.3) {
            const front = Math.sin(a) > 0;
            return (front ? 0.95 : 0.4) * (1 - k * 0.4) * fade;
          }
        }
        return 0;
      };
    },
  };
}

function sparkDef(): Def {
  const chips = Array.from({ length: 7 }, (_, k) => ({ a: hash(k + 5) * TAU, sp: 0.6 + hash(k + 11) * 0.6 }));
  return {
    box: [16, 16, 16, 16],
    field: (p) => {
      const grow = Math.min(1, p / 0.18);
      const thin = 1 - p;
      const L = 13 * grow * (1 - p * 0.4);
      return (u, v) => {
        const au = Math.abs(u);
        const av = Math.abs(v);
        const core = 3.2 * thin;
        if (u * u + v * v < core * core) return 1;
        // A four-pointed star, long across, shorter up and down, short diagonals.
        if (av < 0.7 * thin + 0.3 && au < L) return 1 - (au / L) * 0.6;
        if (au < 0.7 * thin + 0.3 && av < L * 0.7) return 1 - (av / (L * 0.7)) * 0.6;
        if (Math.abs(au - av) < 0.6 && au < L * 0.35) return 0.6;
        for (const c of chips) {
          const r = 4 + p * 16 * c.sp;
          if (
            Math.abs(u - Math.cos(c.a) * r) < 0.6 &&
            Math.abs(v - Math.sin(c.a) * r * 0.8 - p * p * 6) < 0.6
          )
            return 0.75 * thin;
        }
        return 0;
      };
    },
  };
}

function clashDef(): Def {
  return {
    box: [20, 16, 20, 16],
    field: (p) => {
      const grow = Math.min(1, p / 0.15);
      const fade = fadeAfter(p, 0.3);
      const L = 15 * grow;
      return (u, v) => {
        // Two blade flashes crossed, a ring of struck air, a white-hot core.
        if (u * u + v * v < (3.5 * (1 - p)) ** 2) return 1;
        for (const s of [0.6, -0.6]) {
          const d = segDist(u, v, -L, -L * s, L, L * s);
          if (d < 0.7) return 0.95 * fade;
          if (d < 1.6) return 0.4 * fade;
        }
        const ring = Math.abs(Math.hypot(u, v * 1.3) - (4 + p * 13));
        if (ring < 0.7) return 0.7 * fade;
        return 0;
      };
    },
  };
}

function shatterDef(): Def {
  const shards = Array.from({ length: 14 }, (_, k) => ({
    a: (k / 14) * TAU + hash(k) * 0.4,
    sp: 0.55 + hash(k + 30) * 0.55,
    spin: (hash(k + 50) - 0.5) * 9,
    w: 1.6 + hash(k + 70) * 1.4,
  }));
  return {
    box: [36, 30, 36, 28],
    field: (p) => {
      const fade = fadeAfter(p, 0.55);
      return (u, v) => {
        if (p < 0.22 && u * u + v * v < (10 * (1 - p / 0.22)) ** 2) return 1;
        for (const s of shards) {
          const r = 6 + p * 30 * s.sp;
          const x = u - Math.cos(s.a) * r;
          const y = v - (Math.sin(s.a) * r * 0.7 + p * p * 20);
          const a = s.a + s.spin * p;
          const ru = x * Math.cos(a) + y * Math.sin(a);
          const rv = -x * Math.sin(a) + y * Math.cos(a);
          if (Math.abs(ru) < s.w && Math.abs(rv) < 1.1) return 0.85 * fade;
        }
        // The broken ring of the guard, gaps opening as it bursts.
        const rr = 9 + p * 8;
        const d = Math.abs(Math.hypot(u, v * 1.2) - rr);
        const a = Math.atan2(v, u);
        if (d < 1 && Math.sin(a * 5 + 1) > -0.1 + p) return 0.6 * fade;
        return 0;
      };
    },
  };
}

/**
 * A tell lies on the ground plane pointing right (unsquashed); `drawTell` turns it to its heading
 * and squashes it onto the isometric ground. `full`: all round the attacker (an area blow).
 */
function tellDef(R: number, brk: boolean, full: boolean): Def {
  const half = full ? Math.PI + 0.2 : brk ? 0.72 : 0.46;
  return {
    res: 1,
    box: [full ? R + 4 : 6, R + 4, R + 4, R + 4],
    field: (p) => (u, v) => {
      const d = Math.hypot(u, v);
      const da = Math.abs(Math.atan2(v, u));
      if (da > half + 0.08) return 0;
      // A break's reach is jagged, an attack's clean.
      const edge = brk ? R * (0.94 + 0.06 * Math.abs(((da * 9) % 2) - 1)) : R;
      if (d > edge + 0.6 || d < 4) return 0;
      const side = (half - da) * d;
      if (edge - d < 1.4 || side < 1.2) return 0.78;
      // The strike gathering out from the attacker: a bright front over a filled wake.
      const front = R * p;
      if (Math.abs(d - front) < 1.3) return 0.98;
      if (d < front) return 0.36;
      return 0.17;
    },
  };
}

function shackleDef(): Def {
  return {
    box: [20, 12, 20, 12],
    field: (p) => {
      const close = 1 + 0.7 * (1 - Math.min(1, p / 0.35));
      const fade = fadeAfter(p, 0.7);
      return (u, v) => {
        const ex = u / (14 * close);
        const ey = v / (6 * close);
        const r = Math.hypot(ex, ey);
        const d = Math.abs(r - 1) * 8;
        if (d > 1.6) return 0;
        // Links in turn: one seen flat (an open oval), the next edge-on (a bar).
        const a = (Math.atan2(ey, ex) / TAU + 1) * 14;
        const link = Math.floor(a) % 2;
        const f = a % 1;
        if (link) return d < 0.6 && f > 0.1 && f < 0.9 ? 0.9 * fade : 0;
        const ring = Math.abs(Math.hypot((f - 0.5) * 3, d / 1.4) - 1);
        return ring < 0.35 ? 0.8 * fade : 0;
      };
    },
  };
}

function soundDef(): Def {
  return {
    box: [4, 22, 44, 22],
    field: (p) => {
      const fade = fadeAfter(p, 0.6);
      return (u, v) => {
        if (u < 2) return 0;
        const a = Math.abs(Math.atan2(v, u));
        if (a > 0.75) return 0;
        const d = Math.hypot(u, v * 1.15);
        for (let k = 0; k < 4; k++) {
          const rr = 4 + ((p * 1.5 + k / 4) % 1) * 38;
          // Each wave thins toward its ends, and wavers like a plucked string.
          const w = 0.9 * (1 - a / 0.75) + 0.2;
          if (Math.abs(d - rr - Math.sin(a * 9 + k) * 0.8) < w) return (0.9 - (rr / 42) * 0.4) * fade;
        }
        return 0;
      };
    },
  };
}

function smokeDef(): Def {
  const puffs = Array.from({ length: 10 }, (_, k) => ({
    x: (hash(k + 3) - 0.5) * 30,
    y: -hash(k + 13) * 26,
    r: 5 + hash(k + 23) * 5,
    d: hash(k + 33) * 0.25,
  }));
  return {
    box: [26, 46, 26, 8],
    field: (p) => {
      const fade = fadeAfter(p, 0.55);
      return (u, v) => {
        let best = 0;
        for (const q of puffs) {
          const g = clamp01((p - q.d) / 0.35);
          if (!g) continue;
          const r = q.r * (0.4 + 0.8 * g);
          // Billowing out from the burst, the cloud drifting up as it thins.
          const d = Math.hypot(u - q.x * g, v - ((q.y - 6) * g - p * 10)) / r;
          if (d < 1) best = Math.max(best, 0.4 + 0.45 * (1 - d * d));
        }
        return best * fade;
      };
    },
  };
}

function caltropsDef(): Def {
  const bits = Array.from({ length: 12 }, (_, k) => ({
    a: hash(k + 2) * TAU,
    r: Math.sqrt(hash(k + 8)) * 22,
    t: hash(k + 17) * 0.3,
  }));
  return {
    box: [26, 18, 26, 14],
    field: (p) => {
      const fade = fadeAfter(p, 0.8);
      return (u, v) => {
        for (const b of bits) {
          const g = clamp01((p - b.t) / 0.25);
          if (!g) continue;
          const x = Math.cos(b.a) * b.r * g;
          // Tossed out in an arc, bouncing once where it lands.
          const y = Math.sin(b.a) * b.r * 0.5 * g - Math.sin(g * Math.PI) * 8;
          const du = Math.abs(u - x);
          const dv = Math.abs(v - y);
          if ((du < 0.6 && dv < 1.8) || (dv < 0.6 && du < 1.8)) return 0.85 * fade;
          if (Math.abs(du - dv) < 0.5 && du < 1.3) return 0.55 * fade;
        }
        return 0;
      };
    },
  };
}

function dharmaDef(R: number): Def {
  return {
    res: 1,
    box: [R + 4, R / 2 + 4, R + 4, R / 2 + 4],
    field: (p) => {
      const grow = Math.min(1, p / 0.25);
      const fade = fadeAfter(p, 0.65);
      const turn = p * 2.2;
      return (u, v) => {
        const gx = u;
        const gy = v * 2;
        const d = Math.hypot(gx, gy) / grow;
        if (d > R + 2) return 0;
        const a = Math.atan2(gy, gx) - turn;
        if (Math.abs(d - R) < 1.6) return 0.9 * fade;
        if (Math.abs(d - R * 0.82) < 0.8) return 0.5 * fade;
        if (d < 5) return d < 3 ? 0.95 * fade : 0.6 * fade;
        // Eight spokes, each ending in a knob on the rim.
        const spoke = Math.abs((((a / TAU) * 8 + 8.5) % 1) - 0.5) * (TAU / 8) * d;
        if (spoke < 0.9 && d < R * 0.82) return 0.75 * fade;
        if (spoke < 2.2 && Math.abs(d - R * 0.92) < 1.8) return 0.8 * fade;
        return d < R ? 0.15 * fade : 0;
      };
    },
  };
}

/** The def for a sheet spec (cached by its key). */
function defOf(s: SheetSpec): Def {
  switch (s.kind) {
    case 'slash':
      return slashDef(s.weapon ?? 'jian', s.arc ?? 'cleave');
    case 'thrust':
      return thrustDef(s.weapon ?? 'jian');
    case 'palm':
      return palmDef(false);
    case 'palmWave':
      return palmDef(true);
    case 'qi':
      return qiDef(s.dir ?? 0);
    case 'slam':
      return slamDef(s.r ?? 30);
    case 'heal':
      return healDef();
    case 'buff':
      return buffDef();
    case 'spark':
      return sparkDef();
    case 'clash':
      return clashDef();
    case 'shatter':
      return shatterDef();
    case 'tell':
      return tellDef(s.r ?? 40, !!s.brk, s.dir === -1);
    case 'shackle':
      return shackleDef();
    case 'sound':
      return soundDef();
    case 'smoke':
      return smokeDef();
    case 'caltrops':
      return caltropsDef();
    case 'dharma':
      return dharmaDef(s.r ?? 40);
    case 'iceArc':
    case 'iceShards':
    case 'frost':
    case 'iceShell':
    case 'iceShatter':
    case 'frostGlint':
      return iceDef(s.kind, s.arc, s.r);
  }
}

function rampFor(tint: string): Ramp {
  if (tint in RAMPS) return RAMPS[tint as FxElement];
  if (tint === 'tell:attack') return TELL_RAMPS.attack;
  if (tint === 'tell:break') return TELL_RAMPS.break;
  if (tint === 'ice') return ICE_RAMP;
  return rampOf(tint);
}

/** Tone thresholds: below the first nothing; then shade, body, lit, core. */
const STEPS = [0.14, 0.32, 0.56, 0.8] as const;
const ALPHA = [0, 150, 225, 255, 255] as const;

const tone = (i: number): number =>
  i < STEPS[0] ? 0 : i < STEPS[1] ? 1 : i < STEPS[2] ? 2 : i < STEPS[3] ? 3 : 4;

interface Frame {
  c: HTMLCanvasElement;
  /** Canvas pixels per art pixel. */
  res: number;
  /** Where the cropped frame's top-left sits from the anchor (art pixels). */
  x: number;
  y: number;
}

/**
 * Effects that cling to a figure (density.ts), drawn on the figure grid (G2, two canvas pixels per
 * art pixel): a weapon's trail through a swing or thrust, the qi spiralling round the body, the
 * frost crescent of a cut, the ice a frozen foe is held in and frost gathering on the blade. Bursts,
 * sparks, rings, circles, projectiles and ground decals belong to the world and are drawn on G1.
 */
const CLINGS: ReadonlySet<SheetKind> = new Set<SheetKind>([
  'slash',
  'thrust',
  'buff',
  'iceArc',
  'iceShell',
  'frostGlint',
]);

/** Drawn frames by sheet key and frame; the oldest let go past `MAX_FRAMES`. */
const FRAMES_CACHE = new Map<string, Frame | null>();
const MAX_FRAMES = 1400;
const DEFS = new Map<string, Def>();

/** Bucketed size (quarters) so near sizes share frames. */
export const sizeBucket = (s: number): number => Math.max(0.5, Math.round(s * 4) / 4);

function keyOf(s: SheetSpec): string {
  return `${s.kind}|${s.weapon ?? ''}|${s.arc ?? ''}|${s.dir ?? ''}|${s.brk ? 1 : 0}|${s.r ?? ''}|${s.tint}|${sizeBucket(s.size)}|${s.flip ? 1 : 0}`;
}

/** Draws one frame of a sheet (cropped to what it lights), or null when it is empty. */
function render(s: SheetSpec, f: number): Frame | null {
  const defKey = `${s.kind}|${s.weapon ?? ''}|${s.arc ?? ''}|${s.dir ?? ''}|${s.brk ? 1 : 0}|${s.r ?? ''}`;
  let def = DEFS.get(defKey);
  if (!def) DEFS.set(defKey, (def = defOf(s)));
  const size = sizeBucket(s.size);
  // Density (density.ts): effects that cling to a figure on its grid (G2), the rest on the world's (G1).
  const RES = density1x() || !CLINGS.has(s.kind) ? 1 : (def.res ?? 2);
  const [l0, t, r0, b] = def.box.map((x) => x * size) as [number, number, number, number];
  // Mirrored, what reached right of the anchor reaches left.
  const [l, r] = s.flip ? [r0, l0] : [l0, r0];
  const W = Math.ceil((l + r) * RES);
  const H = Math.ceil((t + b) * RES);
  const field = def.field((f + 0.5) / FRAMES);
  const flip = s.flip ? -1 : 1;
  const at = (u: number, v: number) => field((u * flip) / size, v / size);
  const ramp = rampFor(s.tint);
  const cols = [null, ramp.shade, ramp.body, ramp.light, ramp.core] as const;
  const alpha = def.alpha ?? ALPHA;
  const data = new Uint8ClampedArray(W * H * 4);
  let x0 = W;
  let y0 = H;
  let x1 = -1;
  let y1 = -1;
  const e = 0.75;
  for (let py = 0; py < H; py++) {
    const v = (py + 0.5) / RES - t;
    for (let px = 0; px < W; px++) {
      const u = (px + 0.5) / RES - l;
      const i = at(u, v);
      let k = tone(i);
      if (!k) continue;
      // Lit from the upper right: an edge facing the light a tone up, one turned away a tone down.
      const g = at(u - e, v + e) - at(u + e, v - e);
      if (g > 0.12 && k < 4) k++;
      else if (g < -0.12 && k > 1) k--;
      const c = cols[k]!;
      const o = (py * W + px) * 4;
      data[o] = c[0];
      data[o + 1] = c[1];
      data[o + 2] = c[2];
      data[o + 3] = alpha[k];
      if (px < x0) x0 = px;
      if (px > x1) x1 = px;
      if (py < y0) y0 = py;
      if (py > y1) y1 = py;
    }
  }
  if (x1 < 0) return null;
  if (def.outline) {
    // The rim: every empty art pixel beside a lit one, in the shade tone (whole art pixels, so it
    // stays on the grid).
    const lit = (x: number, y: number) =>
      x >= 0 && y >= 0 && x < W && y < H && data[(y * W + x) * 4 + 3]! > 0;
    const rim: number[] = [];
    for (let py = 0; py < H; py += RES)
      for (let px = 0; px < W; px += RES) {
        if (lit(px, py)) continue;
        if (lit(px - RES, py) || lit(px + RES, py) || lit(px, py - RES) || lit(px, py + RES))
          rim.push(px, py);
      }
    for (let i = 0; i < rim.length; i += 2)
      for (let dy = 0; dy < RES; dy++)
        for (let dx = 0; dx < RES; dx++) {
          const px = rim[i]! + dx;
          const py = rim[i + 1]! + dy;
          if (px >= W || py >= H) continue;
          const o = (py * W + px) * 4;
          [data[o], data[o + 1], data[o + 2]] = ramp.shade;
          data[o + 3] = alpha[1];
          x0 = Math.min(x0, px);
          x1 = Math.max(x1, px);
          y0 = Math.min(y0, py);
          y1 = Math.max(y1, py);
        }
  }
  // Crop to whole art pixels, so the frame still lands on the art grid.
  x0 -= x0 % RES;
  y0 -= y0 % RES;
  const cw = Math.min(W - x0, x1 - x0 + RES - ((x1 - x0) % RES));
  const ch = Math.min(H - y0, y1 - y0 + RES - ((y1 - y0) % RES));
  const c = canvas(cw, ch);
  const img = new ImageData(cw, ch);
  for (let y = 0; y < ch; y++) {
    const from = ((y0 + y) * W + x0) * 4;
    img.data.set(data.subarray(from, from + cw * 4), y * cw * 4);
  }
  ctx2d(c).putImageData(img, 0, 0);
  return { c, res: RES, x: x0 / RES - l, y: y0 / RES - t };
}

/** Frame `f` of a sheet, drawn on first use. */
export function sheetFrame(s: SheetSpec, f: number): Frame | null {
  const fr = Math.max(0, Math.min(FRAMES - 1, Math.floor(f)));
  const key = `${keyOf(s)}#${fr}`;
  let got = FRAMES_CACHE.get(key);
  if (got === undefined) {
    got = render(s, fr);
    FRAMES_CACHE.set(key, got);
    if (FRAMES_CACHE.size > MAX_FRAMES) FRAMES_CACHE.delete(FRAMES_CACHE.keys().next().value!);
  }
  return got;
}

/** Draws a sheet at phase `k` (0–1) with its anchor at (`x`, `y`) on the art grid. */
export function drawSheet(g: CanvasRenderingContext2D, s: SheetSpec, k: number, x: number, y: number): void {
  const fr = sheetFrame(s, k * FRAMES);
  if (!fr) return;
  g.drawImage(fr.c, Math.round(x) + fr.x, Math.round(y) + fr.y, fr.c.width / fr.res, fr.c.height / fr.res);
}

/**
 * Draws an enemy's wind-up tell at progress `k` under the attacker at (`x`, `y`), turned toward
 * (`dx`, `dy`) on screen and laid on the isometric ground. `reach` in art pixels (bucketed).
 */
export function drawTell(
  g: CanvasRenderingContext2D,
  k: number,
  x: number,
  y: number,
  dx: number,
  dy: number,
  reach: number,
  brk: boolean,
  full: boolean,
): void {
  const r = reach < 42 ? 34 : reach < 62 ? 52 : 72;
  const s: SheetSpec = {
    kind: 'tell',
    tint: brk ? 'tell:break' : 'tell:attack',
    size: 1,
    brk,
    r,
    dir: full ? -1 : 0,
  };
  const fr = sheetFrame(s, k * FRAMES);
  if (!fr) return;
  g.save();
  g.translate(Math.round(x), Math.round(y));
  g.scale(1, 0.5);
  if (!full) g.rotate(Math.atan2(dy * 2, dx));
  g.drawImage(fr.c, fr.x, fr.y, fr.c.width / fr.res, fr.c.height / fr.res);
  g.restore();
}

/** A heading on screen (dx, dy) in sixteenths of a turn, for qi and tells. */
export const dirOf = (dx: number, dy: number): number =>
  ((Math.round((Math.atan2(dy, dx) / TAU) * 16) % 16) + 16) % 16;

/** Hit spark size by how hard a blow is (its damage against the target's whole health) and a crit. */
export function sparkSize(damage: number, maxHp: number, crit: boolean): number {
  const f = damage / Math.max(1, maxHp);
  const tier = (f < 0.04 ? 0 : f < 0.1 ? 1 : f < 0.2 ? 2 : 3) + (crit ? 1 : 0);
  return [0.75, 1, 1.5, 2, 2.5][Math.min(4, tier)]!;
}
